/**
 * EntitySocialsFields - Collapsible social media link inputs for spot mode new-entity creation.
 * Only rendered when the user is creating a new entity (not spotting an existing one).
 *
 * @atomic - Single responsibility: social link form fields
 * @idempotent - Pure component
 */
import { memo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { getInputClasses, labelClasses } from '../../styles/inputStyles'
import { trackEvent } from '../../utils/analytics'

const EntitySocialsFields = memo(function EntitySocialsFields({ socials = {}, onChange }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const handleChange = useCallback((field, value) => {
    onChange({ ...socials, [field]: value || undefined })
  }, [socials, onChange])

  const handleToggle = () => {
    const next = !expanded
    setExpanded(next)
    trackEvent('SpotMode', next ? 'socials_expand' : 'socials_collapse', 'spot_panel')
  }

  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={handleToggle}
        onTouchEnd={(e) => { e.preventDefault(); handleToggle() }}
        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors w-full text-left"
      >
        <svg
          className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {t('entity.socials.addLinks', 'Add social links (optional)')}
      </button>

      {expanded && (
        <div className="mt-2 space-y-2">
          {[
            { field: 'instagram_url', label: t('entity.socials.instagram', 'Instagram'), placeholder: 'https://instagram.com/...' },
            { field: 'tiktok_url',   label: t('entity.socials.tiktok',    'TikTok'),    placeholder: 'https://tiktok.com/@...' },
            { field: 'facebook_url', label: t('entity.socials.facebook',  'Facebook'),  placeholder: 'https://facebook.com/...' },
            { field: 'x_url',        label: t('entity.socials.x',         'X'),         placeholder: 'https://x.com/...' },
          ].map(({ field, label, placeholder }) => (
            <div key={field}>
              <label className={labelClasses}>{label}</label>
              <input
                type="url"
                value={socials[field] ?? ''}
                onChange={(e) => handleChange(field, e.target.value.trim())}
                placeholder={placeholder}
                className={getInputClasses(false)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
})

export default EntitySocialsFields
